// Libs
import { useEffect, useState } from "react";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { toast } from "sonner";
import { useMutation, useQuery } from "@tanstack/react-query";
import * as Yup from "yup";
// UI-Components
import {
  OutletHeading,
  OutletSubHeading,
} from "../../UI-Components/OutletWrapper";
import { Label } from "../../UI-Components/Label";
import { Input } from "../../UI-Components/Input";
import { Button } from "../../UI-Components/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogIcon,
  DialogTitle,
} from "../../UI-Components/Dialog";
// Types
import { HCPCSCode, PreAuthForm } from "../../Libs/Types/PreAuth";
import {
  FailureResponseWrapper,
  GetHcpcsCodeAPI_Response,
  PatientById_Response,
  PreauthWithDocuments_Response,
  SuccessResponseWrapper,
} from "../../Libs/Types/API-Responses";
import { CreatePreAuthPayload } from "../../Libs/Types/API-Payloads";
// Components
import DocumentUpload from "../DocumentUpload";
import SuccessIcon from "../../UI-Components/SuccessIcon";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "../../UI-Components/Select";
// Endpoints
import { apiHcpcsCodeList } from "../../Endpoints/Hcpcs";
import { apiGetPatientByUniqueId } from "../../Endpoints/Patients";
import { apiAddPreAuth } from "../../Endpoints/Preauthorizations";

export default function PreAuth() {
  const [files, setFiles] = useState<File[]>([]);
  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [hcpcsData, setHcpcsData] = useState<HCPCSCode[]>([]);
  const [uniquePatientId, setUniquePatientId] = useState<string>("");
  const [initialValues, setInitialValues] = useState<PreAuthForm>();
  const [preAuthStatus, setPreAuthStatus] = useState<string>("");
  const [formReset, setFormReset] = useState<() => void>();

  // Fetching User Profile
  const { data: getHCPCSData } = useQuery({
    queryKey: ["/get-hcpcs"],
    queryFn: apiHcpcsCodeList,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  // API Response Handling
  const { mutateAsync: createPreAuth, isPending: isPreAuthSubmitting } =
    useMutation({
      mutationFn: apiAddPreAuth,
      retry: 0,
      onSuccess: async (data) => {
        const respData: SuccessResponseWrapper<PreauthWithDocuments_Response> =
          data.data;
        setPreAuthStatus(respData.data.preauthorization.status);
        setDialogOpen(true);
      },
      onError: (error: any) => {
        const errorData: FailureResponseWrapper = error?.response?.data;
        toast.error(errorData?.message ?? "Something Went Wrong", {
          duration: 3000,
        });
      },
      throwOnError: false,
    });

  // API Response Handling
  const {
    mutateAsync: getPatientDetails,
    isSuccess: isPatientDataFetched,
    isPending: isPatientDetailsFetching,
    reset: resetPatientFetch,
  } = useMutation({
    mutationFn: apiGetPatientByUniqueId,
    retry: 1,
    onSuccess: async (data) => {
      const respData: SuccessResponseWrapper<PatientById_Response> = data.data;
      setInitialValues({
        patient_id: respData.data.patient_id,
        firstName: respData.data.first_name,
        lastName: respData.data.last_name,
        provider: respData.data.insurance_id,
        hcpcsCode: "",
      });
    },
    onError: (error: any) => {
      const errorData: FailureResponseWrapper = error?.response?.data;
      toast.error(errorData?.message ?? "Something Went Wrong", {
        duration: 3000,
      });
    },
    throwOnError: false,
  });

  // Handle Dialog Close And Calls Reset On Close
  const handleDialogClose = () => {
    setDialogOpen(false);
    handleReset();
  };

  // Handle Fetch Details Btn Click
  const handleDetailsBtnClick = async () => {
    await getPatientDetails(uniquePatientId);
  };

  // Handle PreAuth Form Submit
  const handlePreAuthFormSubmit = async (values: PreAuthForm) => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });
    const payload: CreatePreAuthPayload = {
      patient_id: values.patient_id,
      hcpcs_code_id: values.hcpcsCode,
    };
    formData.append("payload", JSON.stringify(payload));
    await createPreAuth(formData);
  };

  // PreAuth Validation Schema
  const PreAuthFormValidationSchema = Yup.object().shape({
    firstName: Yup.string()
      .min(2, "Minimum 2 characters required")
      .required("First name is required"),
    lastName: Yup.string()
      .min(2, "Minimum 2 characters required")
      .required("Last name is required"),
    provider: Yup.string().required("Provider is required"),
    hcpcsCode: Yup.string()
      .min(1, "Minimum 2 character required")
      .required("HCPCS code is required"),
  });

  // Set States back to initial State
  const handleReset = () => {
    formReset && formReset();
    setFiles([]);
    setInitialValues(undefined);
    setUniquePatientId("");
    setPreAuthStatus("");
    resetPatientFetch();
  };

  useEffect(() => {
    if (getHCPCSData !== undefined) {
      const getCodes: SuccessResponseWrapper<GetHcpcsCodeAPI_Response[]> =
        getHCPCSData.data;
      setHcpcsData(getCodes.data);
    }
  }, [getHCPCSData]);
  return (
    <>
      <div className="px-2">
        <OutletHeading>Medical Insurance Claim Automation</OutletHeading>
        <OutletSubHeading className="mt-2">Fill the details</OutletSubHeading>
        <div className="grid md:grid-cols-2 gap-x-4">
          <div className="my-1 md:col-span-2">
            <Label
              className="block text-primary text-xs my-2 font-semibold"
              htmlFor="uniquePatientId"
            >
              Unique Patient ID
            </Label>
            <div className="flex gap-4">
              <Input
                // as={Input}
                disabled={isPatientDetailsFetching}
                value={uniquePatientId}
                onChange={(e: any) => setUniquePatientId(e.target.value)}
                className="w-full py-2 border-primary text-title outline-none rounded transition bg-white"
                type="text"
                id="uniquePatientId"
                name="uniquePatientId"
                // required
                placeholder="Enter Unique Patient ID"
              />
              <Button
                type="button"
                className="w-full lg:w-1/4"
                onClick={handleDetailsBtnClick}
                disabled={uniquePatientId === "" || isPatientDetailsFetching}
              >
                {isPatientDetailsFetching ? "Fetching..." : "Get Details"}
              </Button>
            </div>
          </div>
        </div>

        {isPatientDataFetched && uniquePatientId !== "" && (
          <Formik
            initialValues={initialValues!}
            onSubmit={(values, { resetForm }) => {
              handlePreAuthFormSubmit(values);
              setFormReset(resetForm);
            }}
            validationSchema={PreAuthFormValidationSchema}
          >
            {({ values, setFieldValue, isValid }) => (
              <Form className="w-full">
                <div className="grid md:grid-cols-2 gap-x-4">
                  <div className="my-1">
                    <Label
                      className="block text-primary text-xs my-2 font-semibold"
                      htmlFor="firstName"
                    >
                      First Name
                    </Label>
                    <Field
                      as={Input}
                      className="w-full py-2 border-primary text-title outline-none rounded transition bg-white"
                      type="text"
                      id="firstName"
                      name="firstName"
                      // required
                      disabled={true}
                      placeholder="Enter First Name"
                    />
                    <ErrorMessage
                      name="firstName"
                      component="p"
                      className="text-red-500 text-sm"
                    />
                  </div>
                  <div className="my-1">
                    <Label
                      className="block text-primary text-xs my-2 font-semibold"
                      htmlFor="lastName"
                    >
                      Last Name
                    </Label>
                    <Field
                      as={Input}
                      className="w-full py-2 border-primary text-title outline-none rounded transition bg-white"
                      type="text"
                      id="lastName"
                      name="lastName"
                      // required
                      disabled={true}
                      placeholder="Enter Last Name"
                    />
                    <ErrorMessage
                      name="lastName"
                      component="p"
                      className="text-red-500 text-sm"
                    />
                  </div>
                  <div className="my-1">
                    <Label
                      className="block text-primary text-xs my-2 font-semibold"
                      htmlFor="provider"
                    >
                      Provider
                    </Label>
                    <Field
                      as={Input}
                      className="w-full py-2 border-primary text-title outline-none rounded transition bg-white"
                      type="text"
                      id="provider"
                      name="provider"
                      // required
                      disabled={true}
                      placeholder="Enter Provider"
                    />
                    <ErrorMessage
                      name="provider"
                      component="p"
                      className="text-red-500 text-sm"
                    />
                  </div>
                  <div className="my-1">
                    <Label
                      className="block text-primary text-xs my-2 font-semibold"
                      htmlFor="hcpcsCode"
                    >
                      HCPCS Code
                    </Label>
                    <Select
                      value={values.hcpcsCode}
                      onValueChange={(val) => setFieldValue("hcpcsCode", val)}
                    >
                      <SelectTrigger
                        className="border-primary"
                        isSelected={values.hcpcsCode !== "" ? true : false}
                      >
                        <SelectValue placeholder="Select Code" />
                      </SelectTrigger>

                      <SelectContent>
                        <SelectItem value="Select" disabled>
                          Select Code
                        </SelectItem>
                      </SelectContent>

                      {hcpcsData.length > 0 && (
                        <SelectContent>
                          {hcpcsData.map(({ id, hcpcs_code }) => (
                            <SelectItem key={id} value={String(id)}>
                              {hcpcs_code}
                            </SelectItem>
                          ))}

                          <SelectSeparator />
                        </SelectContent>
                      )}
                    </Select>
                    <ErrorMessage
                      name="hcpcsCode"
                      component="p"
                      className="text-red-500 text-sm"
                    />
                  </div>
                  <div className="my-1 md:col-span-2">
                    <Label
                      className="block text-primary text-xs my-2 font-semibold"
                      htmlFor="hcpcsCode"
                    >
                      Upload Document
                    </Label>
                    <DocumentUpload files={files} setFiles={setFiles} />
                  </div>
                </div>
                <div className="mt-5">
                  <Button
                    type="submit"
                    className="w-full lg:w-1/4"
                    disabled={
                      !isValid || files.length === 0 || isPreAuthSubmitting
                    }
                  >
                    {isPreAuthSubmitting ? "Submitting..." : "Submit"}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        )}

        <Dialog open={isDialogOpen} onOpenChange={handleDialogClose}>
          <DialogContent className="max-w-3xl">
            <DialogIcon>
              <SuccessIcon />
            </DialogIcon>
            <DialogHeader>
              <DialogTitle>
                PreAuthorization request submitted successfully
              </DialogTitle>
              <DialogDescription>
                Status: <strong>{preAuthStatus}</strong>
              </DialogDescription>
            </DialogHeader>
            {/* <div className="whitespace-pre-wrap text-center text-primary text-sm">
              Lorem ipsum dolor sit amet.
            </div> */}
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
